import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import accountVerificationService from '../../../services/accountVerificationService';

const ManualVerificationPanel = ({ onVerificationComplete, refreshTrigger }) => {
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [justification, setJustification] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [bulkSelection, setBulkSelection] = useState([]);

  useEffect(() => {
    loadPendingAccounts();
  }, [refreshTrigger]);

  const loadPendingAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await accountVerificationService?.getFailedConfirmations();
      setPendingAccounts(data);
    } catch (err) {
      setError(err?.message || 'Failed to load pending accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleManualVerification = async () => {
    if (!selectedAccount) return;
    if (!justification?.trim()) {
      setError('Justification is required for manual verification');
      return;
    }

    try {
      setProcessingId(selectedAccount?.userId);
      setError('');
      await accountVerificationService?.manuallyVerifyAccount(
        selectedAccount?.userId,
        justification
      );
      alert(`Account ${selectedAccount?.email} manually verified successfully!`);
      setSelectedAccount(null);
      setJustification('');
      await loadPendingAccounts();
      onVerificationComplete?.();
    } catch (err) {
      setError(err?.message || 'Manual verification failed');
    } finally {
      setProcessingId(null);
    }
  };

  const handleBulkVerification = async () => {
    if (bulkSelection?.length === 0) return;
    const justificationText = prompt('Enter justification for bulk verification:');
    if (!justificationText) return;

    try {
      setLoading(true);
      setError('');
      const results = await accountVerificationService?.bulkVerifyAccounts(
        bulkSelection,
        justificationText
      );
      const successCount = results?.filter(r => r?.success)?.length;
      alert(`Successfully verified ${successCount} of ${bulkSelection?.length} accounts`);
      setBulkSelection([]);
      await loadPendingAccounts();
      onVerificationComplete?.();
    } catch (err) {
      setError(err?.message || 'Bulk verification failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleBulkSelection = (userId) => {
    setBulkSelection(prev =>
      prev?.includes(userId)
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  if (loading && pendingAccounts?.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      {bulkSelection?.length > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="CheckSquare" className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-foreground">
              {bulkSelection?.length} account{bulkSelection?.length > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleBulkVerification}>
              <Icon name="Zap" size={14} className="mr-1" />
              Bulk Verify
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkSelection([])}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Accounts Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={bulkSelection?.length === pendingAccounts?.length && pendingAccounts?.length > 0}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        setBulkSelection(pendingAccounts?.map(a => a?.userId));
                      } else {
                        setBulkSelection([]);
                      }
                    }}
                    className="rounded border-border"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Registration Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {pendingAccounts?.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-sm text-muted-foreground">
                    <Icon name="CheckCircle" className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No pending manual verifications</p>
                  </td>
                </tr>
              ) : (
                pendingAccounts?.map((account) => (
                  <tr key={account?.userId} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={bulkSelection?.includes(account?.userId)}
                        onChange={() => toggleBulkSelection(account?.userId)}
                        className="rounded border-border"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{account?.fullName}</p>
                        <p className="text-xs text-muted-foreground">{account?.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">
                        {new Date(account?.registrationDate)?.toLocaleDateString()}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {account?.isExpired ? (
                        <span className="px-2 py-1 text-xs font-bold rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                          EXPIRED
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-bold rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                          PENDING
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        onClick={() => setSelectedAccount(account)}
                        disabled={processingId === account?.userId}
                      >
                        {processingId === account?.userId ? (
                          <Icon name="Loader2" size={14} className="animate-spin" />
                        ) : (
                          <>
                            <Icon name="UserCheck" size={14} className="mr-1" />
                            Verify
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual Verification Modal */}
      {selectedAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground">Manual Account Verification</h3>
              <button
                onClick={() => {
                  setSelectedAccount(null);
                  setJustification('');
                  setError('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Icon name="AlertTriangle" className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Manual Verification Warning</p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      This will bypass email confirmation and immediately activate the account. Provide a clear justification for audit purposes.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Account Details</label>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Name:</span>
                    <span className="text-sm font-medium text-foreground">{selectedAccount?.fullName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <span className="text-sm font-medium text-foreground">{selectedAccount?.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Registration:</span>
                    <span className="text-sm font-medium text-foreground">
                      {new Date(selectedAccount?.registrationDate)?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Justification <span className="text-error">*</span>
                </label>
                <textarea
                  value={justification}
                  onChange={(e) => setJustification(e?.target?.value)}
                  placeholder="Explain why manual verification is required (e.g., verified identity via phone call, document verification, etc.)"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={4}
                />
              </div>

              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3">
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedAccount(null);
                  setJustification('');
                  setError('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleManualVerification}
                disabled={!justification?.trim() || processingId}
              >
                {processingId ? (
                  <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                ) : (
                  <Icon name="CheckCircle" size={16} className="mr-2" />
                )}
                Verify Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualVerificationPanel;
